import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { navAsset } from './navConfig';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(17, 17, 9, 0.35);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 14vh;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 60px;
  padding: 0 20px;
  border-bottom: 1px solid rgba(17, 17, 0, 0.08);
`;

const SearchInput = styled.input`
  flex: 1 0 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  color: #111100;

  &::placeholder {
    color: rgba(17, 17, 7, 0.6);
  }
`;

const Kbd = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(17, 17, 0, 0.06);
  color: rgba(17, 17, 7, 0.6);
  font-size: 12px;
  line-height: 18px;
`;

const EmptyState = styled.div`
  padding: 32px 20px 40px;
  text-align: center;
  color: rgba(17, 17, 7, 0.5);
  font-size: 14px;
  line-height: 20px;
`;

type SearchOverlayProps = {
  onClose: () => void;
};

export function SearchOverlay({ onClose }: SearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <Backdrop onMouseDown={onClose} data-testid="nav_search-overlay">
      <Panel onMouseDown={e => e.stopPropagation()}>
        <InputRow>
          <img src={navAsset('search')} alt="" width={18} height={18} />
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Search emails, forms, pages…"
            data-testid="nav_search_query-input"
          />
          <Kbd>esc</Kbd>
        </InputRow>
        <EmptyState>Start typing to search across your workspace.</EmptyState>
      </Panel>
    </Backdrop>
  );
}
